import { useState, useEffect } from 'react';
import { getVoucherMascotList, exchangeVoucher } from '../api/redeemService';
import { useLanguage } from '@shared/contexts/LanguageContext';

export default function VoucherRedeemPopup({ isOpen, onClose, onSuccess, voucher }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [mascotList, setMascotList] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [encouragement, setEncouragement] = useState('');


  useEffect(() => {
    if (!isOpen || !voucher) return;
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getVoucherMascotList(voucher.item_id);
        if (isMounted) {
          const items = Array.isArray(res) ? res : (res?.items || res?.data || []);
          setMascotList(items);
          setSelectedIndex(0);
        }
      } catch (err) {
        console.error('Failed to fetch voucher mascots:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      setMascotList([]);
      setSelectedIndex(0);
      setShowConfirm(false);
      setShowSuccess(false);
    };
  }, [isOpen, voucher]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const selectedMascot = mascotList[selectedIndex];

  const handleRedeem = async () => {
    try {
      setLoading(true);
      const response = await exchangeVoucher(voucher.user_voucher_id, selectedMascot.reward_id);
      setEncouragement(response.encouragement ?? t('redeem.addedToBag'));
      setShowConfirm(false);
      setShowSuccess(true);
    } catch (error) {
      console.error('Redeem failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdropClick}
    >
      {!showConfirm && !showSuccess && (
        <div
          className="w-full max-w-[400px] p-6 relative bg-[#FCF6ED] rounded-[20px] shadow-lg inline-flex flex-col justify-start items-center gap-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            aria-label={t('redeem.closeAria')}
            className="w-6 h-6 absolute right-6 top-6 overflow-hidden cursor-pointer flex-shrink-0 hover:opacity-60 transition-opacity z-10"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9.25" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" />
              <path d="M7 7L13 13M13 7L7 13" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <div className="w-full text-center text-[#BC313D] text-xl font-bold font-['Noto_Serif_TC'] leading-7 px-8 mt-2">
            {voucher.activity_title ? `${voucher.activity_title} - ${voucher.voucher_name}` : voucher.voucher_name}
          </div>
          {loading && !showConfirm && !showSuccess ? (
            <div className="py-10 text-stone-500">{t('redeem.loading')}</div>
          ) : (
            <div className="w-full flex flex-col items-center gap-6 mt-4">
              {/* Mascot Scrollable List */}
              <div className={`w-full overflow-x-auto pb-4 snap-x hide-scrollbar flex items-start gap-4 ${mascotList.length > 0 ? 'justify-center' : 'justify-start'}`} style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                {mascotList.map((mascot, idx) => {
                  const isActive = selectedIndex === idx;
                  return (
                    <div
                      key={mascot.mascot_id || idx}
                      onClick={() => setSelectedIndex(idx)}
                      className={`flex flex-col items-center gap-3 shrink-0 cursor-pointer transition-all snap-center`}
                      style={{ width: '80px' }}
                    >
                      {/* Card container */}
                      <div
                        className={`w-[80px] h-[80px] bg-white rounded-[16px] flex justify-center items-center p-2 relative`}
                        style={{
                          border: isActive ? '3px solid #BC313D' : '1px solid #E5E5E5',
                          boxShadow: isActive ? '0px 4px 10px rgba(188, 49, 61, 0.2)' : 'none'
                        }}
                      >
                        {mascot.media_url ? (
                          <img
                            src={mascot.media_url}
                            alt={mascot.name || 'Mascot'}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100 rounded-lg" />
                        )}
                      </div>
                      {/* Name */}
                      <span className="text-[#6E625B] text-sm font-medium font-['Noto_Sans_TC'] whitespace-nowrap">
                        {mascot.name ? mascot.name.split('_')[0] : '吉祥物'}
                      </span>
                    </div>
                  );
                })}
                {mascotList.length === 0 && (
                  <div className="w-full py-8 text-center text-stone-500">
                    {t('redeem.noRedeemableMascot')}
                  </div>
                )}
              </div>

              {/* Redeem Button */}
              {mascotList.length > 0 && (
                <button
                  className="px-10 py-2.5 bg-[#BC313D] rounded-full flex justify-center items-center hover:opacity-90 active:scale-95 transition-all shadow-md disabled:opacity-50"
                  onClick={() => setShowConfirm(true)}
                  disabled={loading}
                >
                  <span className="text-white text-base font-bold font-['Noto_Sans_TC'] leading-6 tracking-wider">
                    {t('redeem.redeem')}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirm Popup */}
      {showConfirm && selectedMascot && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 px-4">
          <div className="w-96 p-6 bg-white rounded-[20px] shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10),_0px_42px_42px_0px_rgba(0,0,0,0.09),_0px_95px_57px_0px_rgba(0,0,0,0.05),_0px_168px_67px_0px_rgba(0,0,0,0.01),_0px_263px_74px_0px_rgba(0,0,0,0.00)] inline-flex flex-col justify-start items-center gap-6">
            <div className="self-stretch inline-flex justify-start items-center gap-8">
              <div className="justify-start">
                <span className="text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.useOneVoucher')}</span>
                <span className="text-[#BC313D] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{selectedMascot.name ? selectedMascot.name.split('_')[1] : '行'}｜{selectedMascot.name ? selectedMascot.name.split('_')[0] : '金鼎童子'}</span>
                <span className="text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">】</span>
              </div>
            </div>
            <div className="self-stretch inline-flex justify-start items-center gap-2">
              <div
                className="flex-1 h-11 px-2 pt-0.5 bg-[#BC313D]/10 rounded-[999px] outline outline-1 outline-offset-[-1px] outline-[#BC313D]/30 flex justify-center items-center gap-1 cursor-pointer"
                onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
              >
                <div className="pb-0.5 flex justify-center items-center">
                  <div className="text-center justify-start text-[#BC313D] text-base font-normal font-['SF_Pro'] leading-5">{t('redeem.cancel')}</div>
                </div>
              </div>
              <div
                className={`flex-1 h-11 px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!loading) handleRedeem();
                }}
              >
                <div className="pb-0.5 flex justify-center items-center">
                  <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">
                    {loading ? t('redeem.redeeming') : t('redeem.redeem')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccess && selectedMascot && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 px-4"
          onClick={(e) => {
            e.stopPropagation();
            setShowSuccess(false);
            if (onSuccess) onSuccess();
            else onClose();
          }}
        >
          <div
            className="w-96 px-6 pt-14 pb-6 relative bg-white rounded-[20px] shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10),_0px_42px_42px_0px_rgba(0,0,0,0.09),_0px_95px_57px_0px_rgba(0,0,0,0.05),_0px_168px_67px_0px_rgba(0,0,0,0.01),_0px_263px_74px_0px_rgba(0,0,0,0.00)] inline-flex flex-col justify-start items-center cursor-default"
            onClick={e => e.stopPropagation()}
          >
            <div className="justify-start text-[#171717] text-xl font-medium font-['Noto_Sans_TC'] leading-8 tracking-tight">{t('redeem.redeemSuccess')}</div>
            <div className="justify-start text-[#9B887B] text-base font-normal font-['Noto_Sans_TC'] leading-6">{encouragement}</div>
            <div className="h-10 left-[151px] top-0 absolute inline-flex justify-center items-end gap-[5px]">
              <div className="w-20 h-20 relative bg-white rounded-[999px] shadow-[0px_5px_10px_0px_rgba(55,84,170,0.20)] overflow-hidden flex items-center justify-center">
                {selectedMascot.media_url ? (
                  <img src={selectedMascot.media_url} alt="Success Mascot" className="w-full h-full object-contain bg-white" />
                ) : (
                  <div className="w-20 h-20 left-0 top-0 absolute bg-teal-500" />
                )}
              </div>
            </div>

            {/* Action button */}
            <div
              className="mt-6 self-stretch h-11 px-2 pt-0.5 bg-[#BC313D] rounded-[999px] flex justify-center items-center gap-1 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowSuccess(false);
                if (onSuccess) onSuccess();
                else onClose();
              }}
            >
              <div className="pb-0.5 flex justify-center items-center">
                <div className="text-center justify-start text-white text-lg font-medium font-['Noto_Sans_TC'] leading-7">{t('redeem.confirm')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}} />
    </div>
  );
}
